/**
 * La zona de Cotizaciones: volver a abrir lo que ya se emitio.
 *
 * Es la zona que no existia. Hasta ahora una cotizacion se veia UNA vez --en la
 * pantalla que la acababa de crear-- y al recargar el navegador desaparecia. Un
 * documento con folio que la empresa esta obligada a honrar y que no se puede
 * volver a mirar es medio documento: el agente no puede contestar "que le
 * mandamos a este cliente el martes".
 *
 * ===========================================================================
 * AQUI NO SE CALCULA NADA
 *
 * Los montos vienen congelados [Invariante 5] y el veredicto de aprobacion
 * tambien [migracion 0030]. Esta pantalla no vuelve a llamar al motor y no
 * podria: el piso de margen cambia, y reevaluar haria que una cotizacion
 * emitida sin firma pendiente apareciera necesitandola.
 *
 * ===========================================================================
 * `null` NO ES `false`, Y AQUI SE VE
 *
 * `requiere_aprobacion` llega en tres estados y los tres se pintan distinto:
 *
 *   true   -> el aviso de aprobacion, entero, como en el cotizador.
 *   false  -> nada. No hacia falta firma y no hay nada que decir.
 *   null   -> "no consta". Es una opcion anterior a la 0030, de la que no se
 *             guardo el veredicto.
 *
 * El tercero se dice CON PALABRAS. Pintar un hueco, o tratarlo como un `false`,
 * es exactamente [H59][Invariante 7]: un centinela leido como un cero.
 */

import type {
  CotizacionGuardada,
  CriterioResuelto,
  OpcionGuardada,
  PuntoRuta,
  ResumenCotizacion,
} from '../api';
import { dato, el } from '../dom';
import {
  formatearFecha,
  formatearMoneda,
  formatearTransito,
  type Idioma,
} from '../formato';
import type { Traductor } from '../i18n';
import { resumirCarga } from './carga';
import { pintarLineas } from './cotizacion';
import { mapaRuta } from './mapa';

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

export interface OpcionesListaCotizaciones {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly cotizaciones: readonly ResumenCotizacion[];
  /** De la respuesta, no del rol: la API dice si acoto la lista a las propias. */
  readonly soloMias: boolean;
  readonly cargando: boolean;
  readonly onAbrir: (id: string) => void;
}

export function pintarListaCotizaciones(o: OpcionesListaCotizaciones): HTMLElement {
  const { t } = o;

  const cabecera = el('tr', {}, [
    el('th', { texto: t('cotizaciones.col.folio') }),
    el('th', { texto: t('cotizaciones.col.cliente') }),
    el('th', { texto: t('cotizaciones.col.ruta') }),
    el('th', { texto: t('cotizaciones.col.estado') }),
    // El total de VENTA viaja sin `ve_costo`: es el numero que el cliente ya
    // tiene delante. Lo que `ve_costo` protege es el costo del proveedor.
    el('th', { clase: 'celda-monto', texto: t('cotizaciones.col.venta') }),
    el('th', { texto: t('cotizaciones.col.creada') }),
    // Solo cuando se ven las del equipo: en una lista de las propias, una
    // columna con tu nombre repetido no dice nada.
    o.soloMias ? null : el('th', { texto: t('cotizaciones.col.agente') }),
    el('th', {}),
  ]);

  const filas = o.cotizaciones.map((c) =>
    el('tr', { clase: c.requiere_aprobacion === true ? 'fila-espera' : undefined }, [
      el('td', {}, [el('code', { texto: c.folio })]),
      el('td', { texto: c.cliente.nombre }),
      el('td', {}, [
        c.ruta === null
          ? // La tarifa de origen ya no esta: `rate_record_id` es ON DELETE SET
            // NULL y la cotizacion sigue siendo valida sin ella. Se dice; no se
            // pinta un guion que parezca un dato vacio.
            el('span', { clase: 'tenue', texto: t('cotizaciones.sinRuta') })
          : el('span', { texto: c.ruta }),
        c.equipo === null
          ? null
          : el('span', { clase: 'tenue', texto: ` · ${c.equipo}` }),
      ]),
      el('td', {}, [
        el('span', {
          clase: 'chip chip-estado',
          texto: t(`estadoCotizacion.${c.estado}`),
        }),
        // La naturaleza, cuando no es general. Una cotizacion de peligrosa no
        // se busca igual que una general, y esto es lo que la distingue sin
        // abrirla.
        c.naturaleza === 'GENERAL'
          ? null
          : el('span', {
              clase: `chip ${c.naturaleza === 'PELIGROSA' ? 'chip-critico' : 'chip-atencion'}`,
              texto: t(`carga.naturaleza.${c.naturaleza}`),
            }),
        c.requiere_aprobacion === true
          ? el('span', {
              clase: 'chip chip-aprobacion',
              texto: t('cotizaciones.requiereAprobacion'),
            })
          : null,
      ]),
      el('td', {
        clase: 'celda-monto',
        texto:
          c.total_venta === null
            ? t('cotizaciones.sinTotal')
            : formatearMoneda(c.total_venta, c.moneda_venta, o.idioma),
      }),
      el('td', { texto: formatearFecha(c.creada_en.slice(0, 10), o.idioma) }),
      o.soloMias
        ? null
        : el('td', {}, [
            el('span', { texto: c.agente.nombre }),
            c.agente.propia
              ? el('span', { clase: 'badge', texto: t('cotizaciones.tuya') })
              : null,
          ]),
      el('td', {}, [
        el('button', {
          clase: 'boton boton-plano boton-abrir',
          texto: t('cotizaciones.abrir'),
          attrs: { type: 'button' },
          onClick: () => {
            o.onAbrir(c.id);
          },
        }),
      ]),
    ]),
  );

  return el('section', { clase: 'panel panel-cotizaciones' }, [
    el('header', { clase: 'zona-cabecera' }, [
      el('h2', { texto: t('zona.cotizaciones.nombre') }),
      el('p', {
        clase: 'tenue',
        // Que la lista este acotada se dice SIEMPRE, no solo cuando esta
        // vacia: tres cotizaciones cuando el equipo lleva cien se ve igual que
        // un equipo que no ha cotizado.
        texto: o.soloMias ? t('cotizaciones.soloMias') : t('cotizaciones.deTodos'),
      }),
    ]),

    o.cargando
      ? el('p', { clase: 'tenue', texto: t('app.cargando') })
      : o.cotizaciones.length === 0
        ? el('div', { clase: 'vacio' }, [
            el('p', { texto: t('cotizaciones.vacia') }),
            el('p', { clase: 'tenue', texto: t('cotizaciones.vaciaComo') }),
          ])
        : el('table', { clase: 'tabla-resultados tabla-cotizaciones' }, [
            el('thead', {}, [cabecera]),
            el('tbody', {}, filas),
          ]),
  ]);
}

// ---------------------------------------------------------------------------
// Detalle
// ---------------------------------------------------------------------------

export interface OpcionesDetalleCotizacion {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly detalle: CotizacionGuardada;
  readonly onVolver: () => void;
}

export function pintarDetalleCotizacion(o: OpcionesDetalleCotizacion): HTMLElement {
  const { t, detalle } = o;
  const c = detalle.cotizacion;

  return el('section', { clase: 'panel panel-cotizacion' }, [
    el('header', { clase: 'cotizacion-cabecera' }, [
      el('h2', { texto: `${t('cotizador.folio')} ${c.folio}` }),
      el('span', {
        clase: 'chip chip-estado',
        texto: t(`estadoCotizacion.${c.estado}`),
      }),
      el('span', {
        clase: 'tenue',
        texto: `${t('cotizador.validaHasta')} ${formatearFecha(c.valida_hasta, o.idioma)}`,
      }),
      el('span', {
        clase: 'tenue',
        // El idioma CONGELADO del documento [CLAUDE.md, Idioma]. Puede no ser
        // el de esta pantalla, y por eso se dice: lo que salio de la empresa
        // salio en ese idioma y no se re-renderiza.
        texto: `${t('cotizador.idiomaDocumento')}: ${c.idioma}`,
      }),
      el('button', {
        clase: 'boton boton-plano',
        texto: t('cotizaciones.volver'),
        attrs: { type: 'button' },
        onClick: o.onVolver,
      }),
    ]),

    el('div', { clase: 'cotizacion-ficha' }, [
      dato(t('cotizaciones.col.cliente'), c.cliente.nombre),
      dato(t('cotizaciones.col.agente'), c.agente.nombre),
      dato(
        t('cotizaciones.col.creada'),
        formatearFecha(c.creada_en.slice(0, 10), o.idioma),
      ),
      dato(
        t('cotizaciones.enviada'),
        c.enviada_en === null
          ? t('cotizaciones.sinEnviar')
          : formatearFecha(c.enviada_en.slice(0, 10), o.idioma),
      ),
    ]),

    // Que viajaba, congelado con el documento. Solo cuando dice algo.
    avisoDeCarga(c.carga, t),

    detalle.ve_costo
      ? null
      : el('p', { clase: 'tenue nota-visibilidad', texto: t('cotizador.sinCostos') }),

    ...detalle.opciones.map((op) =>
      pintarOpcion(
        op,
        t,
        o.idioma,
        c.moneda_venta,
        detalle.ve_costo,
        detalle.opciones.length,
      ),
    ),
  ]);
}

/**
 * Rehace el `CriterioResuelto` que el mapa espera a partir de lo que trajo la
 * opcion.
 *
 * `consultado` lleva el nombre y no una cadena vacia: es lo que el mapa pinta
 * cuando algo falta, y una etiqueta vacia se veria como un punto sin nombre.
 * `procedencia` es CURADA porque una cotizacion emitida solo pudo salir de una
 * tarifa cargada; aqui no decide nada --el mapa no la mira-- pero el tipo la
 * pide.
 */
function criterioDeRuta(op: OpcionGuardada): CriterioResuelto {
  const r = op.ruta!;
  const punto = (p: PuntoRuta): CriterioResuelto['pol'] => ({
    nombre: p.nombre,
    pais: p.pais ?? '',
    locode: p.locode,
    consultado: p.nombre,
    procedencia: 'CURADA',
    latitud: p.latitud,
    longitud: p.longitud,
  });
  return {
    pol: punto(r.pol),
    pod: punto(r.pod),
    modo: r.modo ?? 'FCL',
    equipo: {
      codigo: op.equipo ?? '',
      nombre: op.equipo ?? '',
      teu: '',
      consultado: op.equipo ?? '',
    },
    fecha_embarque: '',
  };
}

/** Igual que en el cotizador: PELIGROSA en rojo, las demas en neutro, y con
 *  GENERAL no se pinta nada. */
function avisoDeCarga(
  carga: Record<string, unknown> | undefined,
  t: Traductor,
): HTMLElement | null {
  const resumen = resumirCarga(carga, t);
  if (resumen === null) return null;

  const peligrosa = carga?.naturaleza === 'PELIGROSA';
  return el('div', { clase: `aviso ${peligrosa ? 'aviso-critico' : 'aviso-neutro'}` }, [
    el('strong', { texto: t('carga.enCotizacion') }),
    el('p', { texto: resumen }),
  ]);
}

function pintarOpcion(
  op: OpcionGuardada,
  t: Traductor,
  idioma: Idioma,
  moneda: string,
  veCosto: boolean,
  cuantas: number,
): HTMLElement {
  return el('div', { clase: 'cotizacion-opcion' }, [
    // El encabezado de opcion solo cuando hay mas de una: con una sola seria
    // una linea que dice "opcion 1 de 1".
    cuantas === 1
      ? null
      : el('h3', {
          texto: op.etiqueta ?? op.equipo ?? t('cotizaciones.opcionSinEtiqueta'),
        }),

    el('div', { clase: 'cotizacion-terminos' }, [
      // `dato` se retira solo con un valor nulo, asi que en aereo esta linea no
      // deja hueco: no hay equipo que nombrar. En su sitio van las dos cifras
      // que explican el documento --los kilos que se cobraron y a como-- que es
      // lo que hace falta para releer una factura aerea un año despues.
      dato(t('resultado.equipo'), op.equipo),
      op.aereo === undefined
        ? null
        : dato(
            t('aereo.pesoTarifable'),
            `${op.aereo.peso.tarifable_kg} ${t('aereo.kg')} (${op.aereo.escalon.etiqueta})`,
          ),
      op.aereo?.escalon.precio_por_kg === undefined
        ? null
        : dato(
            t('aereo.porKgColumna'),
            `${formatearMoneda(op.aereo.escalon.precio_por_kg, moneda, idioma)}${t('aereo.porKg')}`,
          ),
      // Por que se cobraron esos kilos y no los de la bascula. Es la pregunta
      // que llega cuando el cliente compara la factura con su packing list, y
      // el documento tiene que contestarla sin que nadie vuelva al catalogo.
      op.aereo?.peso.volumetrico_kg == null
        ? null
        : dato(
            t('aereo.porQueEsePeso'),
            t(`aereo.manda.${op.aereo.peso.manda}`, {
              real: op.aereo.peso.real_kg,
              volumetrico: op.aereo.peso.volumetrico_kg,
            }),
          ),
      dato(
        t('resultado.transito'),
        formatearTransito(op.transito_min_dias, op.transito_max_dias),
      ),
      dato(t('resultado.alcance'), t(`alcance.${op.alcance}`)),
      dato(t('resultado.terminoEntrega'), t(`terminoEntrega.${op.termino_entrega}`)),
      dato(
        t('cotizaciones.diasLibres'),
        [
          op.dias_libres_demora === null
            ? null
            : `${t('resultado.demora')} ${String(op.dias_libres_demora)}`,
          op.dias_libres_detencion === null
            ? null
            : `${t('resultado.detencion')} ${String(op.dias_libres_detencion)}`,
        ]
          .filter((x) => x !== null)
          .join(' · ') || null,
      ),
    ]),

    // El mapa se dibuja con las coordenadas que trajo la API. Cuando la tarifa
    // de origen ya no esta, `ruta` es null y no se pinta nada: es trazabilidad
    // perdida, no un dato de la cotizacion, y una disculpa aqui seria ruido
    // encima de un documento.
    op.ruta === null
      ? null
      : (mapaRuta(criterioDeRuta(op), 'documento', t)?.elemento ?? null),

    pintarVeredicto(op, t),

    pintarLineas(op.lineas, t, idioma, moneda, veCosto),

    el('div', { clase: 'totales' }, [
      dato(
        t('cotizador.venta'),
        formatearMoneda(op.totales.venta, moneda, idioma),
        'total-fila total-venta',
      ),
      op.totales.costo === undefined
        ? null
        : dato(
            t('cotizador.costo'),
            formatearMoneda(op.totales.costo, moneda, idioma),
            'total-fila',
          ),
    ]),
  ]);
}

/**
 * Los tres estados de `requiere_aprobacion`. Ver la cabecera del archivo.
 *
 * `false` no pinta nada a proposito: "esta cotizacion no necesitaba
 * aprobacion" es la situacion normal, y anunciarla en cada folio convertiria
 * el aviso de las que SI la necesitan en una linea mas.
 */
function pintarVeredicto(op: OpcionGuardada, t: Traductor): HTMLElement | null {
  if (op.requiere_aprobacion === null) {
    return el('p', {
      clase: 'aviso aviso-neutro',
      texto: t('cotizaciones.veredictoAusente'),
    });
  }
  if (!op.requiere_aprobacion) return null;

  return el('div', { clase: 'aviso aviso-aprobacion' }, [
    el('strong', { texto: t('cotizador.requiereAprobacion') }),
    op.motivo_aprobacion === null
      ? null
      : el('p', { texto: `${t('cotizador.motivoAprobacion')}: ${op.motivo_aprobacion}` }),
    op.piso_aplicado === null
      ? null
      : el('p', {
          clase: 'tenue',
          // El texto de `piso_margen.descripcion` TAL COMO ESTABA el dia que se
          // cotizo [migracion 0030]. La de hoy explicaria otro precio.
          texto: `${t('cotizador.pisoAplicado')}: ${op.piso_aplicado}`,
        }),
    op.tiene_estimados === true
      ? el('p', { clase: 'tenue', texto: t('cotizaciones.conEstimados') })
      : null,
  ]);
}
